/**
 * A porta entre a tela de estoque e a API do módulo 07. O `fetch` e a queda de
 * erro moram em `@/lib/pedido`.
 */
import { comCorpo, pedir } from '@/lib/pedido';

export interface SaldoDaApi {
  readonly produtoId: string;
  readonly produtoCodigo: string;
  readonly produto: string;
  readonly categoria: string;
  readonly unidade: string;
  readonly critico: boolean;
  /** `deposito` é almoxarifado; `tanque` é o livro do módulo 08 (decisão 13). */
  readonly localTipo: 'deposito' | 'tanque';
  readonly localId: string;
  readonly local: string;
  readonly endereco: string | null;
  readonly estoqueMinimo: number;
  readonly fisico: number;
  readonly reservado: number;
  readonly disponivel: number;
  readonly abaixoDoMinimo: boolean;
  readonly ultimoMovimentoEm: string | null;
}

/** Uma linha do livro do estoque (`GET /estoque/movimentos`). */
export interface MovimentoDaApi {
  readonly id: string;
  readonly tipo: 'entrada' | 'saida' | 'devolucao' | 'transferencia' | 'ajuste_entrada' | 'ajuste_saida';
  readonly quantidade: number;
  readonly produto: string;
  readonly unidade: string;
  readonly deposito: string;
  readonly depositoDestino: string | null;
  readonly documento: string | null;
  readonly motivo: string | null;
  readonly ordemNumero: number | null;
  readonly equipamento: string | null;
  readonly fornecedor: string | null;
  /** Ausentes em servidor anterior: o formulário de movimento traz o fornecedor e o depósito da última entrada. */
  readonly depositoId?: string;
  readonly fornecedorId?: string | null;
  readonly custoUnitario: number | null;
  readonly ocorridoEm: string;
  readonly registradoPor: string | null;
  /** 0088 · o que foi digitado, na embalagem (CX 12), quando houve; `quantidade` é na unidade do item. */
  readonly quantidadeInformada?: number | null;
  readonly unidadeInformada?: string | null;
}

/** 0088 · a embalagem do item e quantas unidades do item cabem nela. */
export interface ConversaoDaApi {
  readonly id: string;
  readonly unidade: string;
  readonly fator: number;
  readonly ativo?: boolean;
  readonly criadoEm?: string;
}

/** 0088 · a aplicação validada: o item serve no modelo inteiro ou numa configuração dele. */
export interface AplicacaoDaApi {
  readonly id: string;
  readonly modeloId: string;
  readonly modelo: string;
  readonly fabricante: string | null;
  readonly configuracaoId: string | null;
  readonly configuracao: string | null;
  readonly observacao: string | null;
  readonly validadoPor: string | null;
  readonly validadoEm: string;
}

export interface ModeloParaAplicacao {
  readonly id: string;
  readonly nome: string;
  readonly fabricante: string | null;
  readonly configuracoes: readonly { readonly id: string; readonly nome: string }[];
}

export interface EnvioDeAplicacao {
  readonly modeloId?: string;
  readonly configuracaoId?: string;
  readonly observacao?: string;
}

export interface ApoioDoEstoque {
  readonly depositos: readonly { id: string; codigo: string; nome: string }[];
  readonly produtos: readonly {
    id: string;
    codigo: string;
    nome: string;
    categoria: string;
    unidade: string;
    /** 0088 · as embalagens ativas do item. Ausente em servidor anterior. */
    conversoes?: readonly ConversaoDaApi[];
  }[];
  readonly fornecedores: readonly { id: string; nome: string }[];
  readonly equipamentos: readonly { id: string; codigo: string; descricao: string }[];
  readonly ordens: readonly { id: string; numero: number; equipamento: string }[];
}

export type TipoDeMovimento = 'entrada' | 'saida' | 'devolucao' | 'transferencia';

export interface EnvioDeMovimento {
  readonly tipo: TipoDeMovimento;
  readonly produtoId: string;
  readonly depositoId: string;
  readonly depositoDestinoId?: string | null;
  readonly quantidade: number;
  /** 0088 · com ela, `quantidade` e `custoUnitario` são na embalagem, e o banco converte. */
  readonly conversaoId?: string | null;
  readonly custoUnitario?: number | null;
  readonly fornecedorId?: string | null;
  readonly documento?: string | null;
  readonly ordemId?: string | null;
  readonly equipamentoId?: string | null;
  readonly motivo?: string | null;
}

/** Sem `codigo` (0131, decisao 57): o sistema cria o codigo do item ao salvar. */
export interface EnvioDeItem {
  readonly nome: string;
  readonly categoria: string;
  readonly unidade: string;
  readonly fabricante?: string | null;
  readonly codigoFabricante?: string | null;
  readonly critico?: boolean;
}

/** O que as preventivas vencidas e as que vencem logo pedem, pela lista técnica. */
export interface NecessidadeDaApi {
  readonly produtoId: string;
  readonly produtoCodigo: string;
  readonly produto: string;
  readonly unidade: string;
  readonly critico: boolean;
  readonly necessario: number;
  readonly disponivel: number;
  readonly falta: number;
  readonly maquinas: number;
  readonly tarefas: number;
  readonly primeiraPara: string | null;
}

export const api = {
  necessidade: () => pedir<{ necessidade: readonly NecessidadeDaApi[] }>('/estoque/necessidade'),
  apoio: () => pedir<ApoioDoEstoque>('/estoque/apoio'),
  saldos: () => pedir<{ saldos: readonly SaldoDaApi[] }>('/estoque/saldos'),
  reservas: () =>
    pedir<{
      reservas: readonly {
        id: string;
        quantidade: number;
        /* Ausentes em servidor anterior; o item do estoque no celular filtra por eles (M22). */
        produtoId?: string;
        depositoId?: string;
        criadoEm?: string;
        produto: string;
        unidade: string;
        deposito: string;
        ordemNumero: number;
        equipamento: string;
      }[];
    }>('/estoque/reservas'),
  movimentos: (filtro: { readonly produtoId?: string; readonly depositoId?: string }) => {
    const q = new URLSearchParams();
    if (filtro.produtoId) q.set('produtoId', filtro.produtoId);
    if (filtro.depositoId) q.set('depositoId', filtro.depositoId);
    return pedir<{ movimentos: readonly MovimentoDaApi[] }>(`/estoque/movimentos${q.size ? `?${q.toString()}` : ''}`);
  },
  movimentar: (envio: EnvioDeMovimento) =>
    comCorpo<{ id: string; quantidade?: number | null }>('POST', '/estoque/movimentos', envio),
  /* 0088 · as embalagens do item e as aplicações validadas. */
  conversoes: (produtoId: string) => pedir<{ conversoes: readonly ConversaoDaApi[] }>(`/estoque/produtos/${produtoId}/conversoes`),
  criarConversao: (produtoId: string, envio: { unidade: string; fator: number }) =>
    comCorpo<{ conversao: ConversaoDaApi }>('POST', `/estoque/produtos/${produtoId}/conversoes`, envio),
  desativarConversao: (id: string) => comCorpo<{ id: string }>('POST', `/estoque/conversoes/${id}/desativacao`, {}),
  aplicacoes: (produtoId: string) =>
    pedir<{ aplicacoes: readonly AplicacaoDaApi[]; modelos: readonly ModeloParaAplicacao[] }>(`/estoque/produtos/${produtoId}/aplicacoes`),
  validarAplicacao: (produtoId: string, envio: EnvioDeAplicacao) =>
    comCorpo<{ id: string }>('POST', `/estoque/produtos/${produtoId}/aplicacoes`, envio),
  retirarAplicacao: (id: string) => pedir<{ retirada: boolean }>(`/estoque/aplicacoes/${id}`, { method: 'DELETE' }),
  contar: (envio: { produtoId: string; depositoId: string; contado: number; observacao?: string | null }) =>
    comCorpo<{ ajustado: boolean; diferenca?: number }>('POST', '/estoque/contagens', envio),
  /* 0131 · o codigo VOLTA do servidor (o banco o cria ao salvar): e ele que o
     retorno da gravacao escreve. */
  criarItem: (envio: EnvioDeItem) => comCorpo<{ id: string; codigo: string }>('POST', '/estoque/produtos', envio),
  criarDeposito: (envio: { nome: string }) => comCorpo<{ id: string; codigo: string }>('POST', '/estoque/depositos', envio),
  posicionar: (envio: { produtoId: string; depositoId: string; endereco?: string | null; estoqueMinimo: number }) =>
    pedir<{ gravado: boolean }>('/estoque/posicoes', { method: 'PUT', body: JSON.stringify(envio) }),
};
